import { useAtomValue, useSetAtom } from "jotai";
import { modelDataAtom } from "../../atoms";
import _ from "lodash";
import apiAgent from "../../api/apiAgent";
import { tMsg } from "../ToastNotifications";
import { publishGroups } from "./publishConfig";
import { updatePublishTimes } from "./utils";
import { Dispatch, SetStateAction } from "react";

const getRelatedModels = (model: any, modelData: any) => {
  const relatedModels =
    model.relationships?.reduce(
      (relArr: string[], relationship: any): string[] => {
        if (!relationship.is_owner) return relArr;

        const modelName = relationship.related_to;
        const getDataForThisModel = modelData.find(
          (m: any) => m.name === modelName
        );
        const addStrings = !getDataForThisModel
          ? []
          : getRelatedModels(getDataForThisModel, modelData);

        return [...relArr, modelName, ...addStrings];
      },
      []
    ) || [];

  return relatedModels;
};

/**
 * Get and publish the models
 * @param models string of models to publish
 * @returns
 */
const usePublisher = (
  groupName: keyof typeof publishGroups,
  setPublishing: Dispatch<SetStateAction<boolean>>
) => {
  // Step 1: get model data from locally stored data atom
  const modelData = useAtomValue(modelDataAtom);
  const { models, singleItems, atom } = publishGroups[groupName];
  const setPublishTimes = useSetAtom(atom);

  // Step 1: validate models
  const validModels = models.reduce((arr: any[], model: string) => {
    const modelSchema = modelData.find((m) => m.name === model);
    if (!modelSchema) return arr;
    return [...arr, modelSchema];
  }, []);

  // Step 2: get model relationships to find out all getAll requests to be made
  // Use loop to find models of subrelations
  const callModels = _.uniq(
    validModels.reduce((arr: string[], model: any): string[] => {
      const relatedModels = getRelatedModels(model, modelData);
      return [...arr, model.name, ...relatedModels];
    }, [])
  );

  // Step 3: Put requests in an array
  const allModelRequests = callModels.map((model) =>
    apiAgent.DataType.getAll(model)
  );

  // Step 4: Make single items reequests
  const allSingleItemRequests = singleItems.map((si) =>
    apiAgent.SingleItem.get(si)
  );

  // Step 5a: Create function for getting children of a parent
  const getOneToManyItems = (
    ownerModel: string,
    ownerId: number,
    childItems: any[]
  ) => {
    const key = `owner_${ownerModel}_id`;
    const relatedItems = childItems.filter((ci) => ci[key] === ownerId);
    return relatedItems;
  };

  // Step 5b: Create function for looping through children to get subitems
  /**
   *
   * @param items
   * @param model
   * @param dataStore
   * @param parentPath model_name > item_id > model_name > item_id > etc..
   * @returns
   */
  const getAllRelationData = (
    items: any[],
    model: any,
    dataStore: any[],
    parentPath: string
  ) => {
    const relationships = model.relationships || [];

    // Get Data relations
    const dataWithRelations = items.map((item: any) => {
      // Get new analytics path
      const pathWithId = `${parentPath}:${item.id}`;

      // Add path for analytics
      const relations = relationships.reduce((obj: any, rship: any) => {
        if (!rship.is_owner) return obj;

        // Get all items for the related model
        const modelItems =
          dataStore.find((ds) => ds.name === rship.related_to)?.data || [];

        // filter items that are related to parent ID
        const relatedItems =
          rship.type === "1-M"
            ? getOneToManyItems(model.name, item.id, modelItems)
            : item[`owner_${rship.related_to}_id`]?.map((id: number) =>
                modelItems.find((mi: any) => mi.id === id)
              );

        // Sort
        relatedItems.sort((a: any, b: any) => {
          const aval = a.sort_id + 1 || 9999999;
          const bval = b.sort_id + 1 || 9999999;
          return aval - bval;
        });

        // Get the model data of the child
        const getRelatedModel = modelData.find(
          (m) => m.name === rship.related_to
        );

        // Get related children of child
        const getSubRelations = getRelatedModel
          ? getAllRelationData(
              relatedItems,
              getRelatedModel,
              dataStore,
              `${pathWithId}>${getRelatedModel.name}`
            )
          : [];

        return {
          ...obj,
          [rship.related_to]: getSubRelations,
        };
      }, {});

      return { ...item, ...relations, analytics_path: pathWithId };
    });

    dataWithRelations.sort((a: any, b: any) => {
      const aval = a.sort_id + 1 || 9999999;
      const bval = b.sort_id + 1 || 9999999;
      return aval - bval;
    });

    return dataWithRelations;
  };

  // Return requesting function
  return async () => {
    try {
      setPublishing(true);
      const results = await Promise.all(allModelRequests);
      // console.log(results);

      // TODO: Can't remember what we omit update/created dates and put them back in.
      // TODO: Was this because of different syntax before?
      const dataStore = callModels.map((modelName, index) => {
        const data = results[index].data?.map((item: any) => {
          const updated_at = item.updated_at;
          const created_at = item.created_at;
          return {
            ..._.omit(item, ["updated_at", "created_at"]),
            updated_at,
            created_at,
          };
        });
        return {
          name: modelName,
          data,
        };
      });
      //console.log("dataStore", dataStore);

      // 1. Get and organize models data
      const modelFilePaths = validModels.reduce((obj: Object, model: any) => {
        const data = dataStore.find((ds) => ds.name === model.name)?.data || [];
        const dataWithRelations = getAllRelationData(
          data,
          model,
          dataStore,
          model.name
        );
        console.log("Publishing", model.name, dataWithRelations);
        return { ...obj, [`endpoints/${model.name}.json`]: dataWithRelations };
      }, {});

      // 2. Get and organize single items data
      const singleItemsResults = await Promise.all(allSingleItemRequests);

      const singleItemsFilePaths = singleItemsResults.reduce(
        (obj, response) => {
          const filePath = response.data?.name
            ? `endpoints/${response.data.name}.json`
            : null;
          if (filePath === null) return obj;
          const data = JSON.parse(response.data.data || `{}`);
          return { ...obj, [filePath]: data };
        },
        {}
      );

      // 3. publish all
      // console.log("Publishing:", {
      //   filepaths_json: {
      //     ...modelFilePaths,
      //     ...singleItemsFilePaths,
      //   },
      // });

      const response = await apiAgent.Models.publish({
        filepaths_json: {
          ...modelFilePaths,
          ...singleItemsFilePaths,
        },
      });

      if (response.status === 200) {
        tMsg.success(
          "Successfully published! Please note that content may take some time to go live."
        );
        updatePublishTimes(groupName, setPublishTimes);
      } else throw response.statusText;
    } catch (err) {
      tMsg.error("Error publishing. See Console for details.");
      console.log(err);
    } finally {
      setPublishing(false);
    }
  };
};

export { usePublisher };
